// The sign-in page in Blueprint (BL-189), before anyone has a session or when it has been lost: one ruled
// sheet on the desk (pages/ux-full signin.html), on catalogued parts. Whose sign-in it is (the company's
// initials beside its name), the title over a rule, Google and Microsoft first, "or", the form (a password,
// or a one-time code by email or text, as the company allows), then the other ways in as Links under a
// hairline. A refusal is a red line; a sent code a done Notice. When a second factor is owed the step
// takes the form's place. The words and the order of steps are Login.tsx's; both run login-model.ts.
// Riverstone keeps Login.tsx and account/TwoFactorStep.tsx.
import { useRef, useState, type FormEvent } from 'react';
import type { TwoFactorMethod } from '@flowology/client';
import { Button, Checkbox, Link, Notice, StateMark, TextField } from '@flowology/ui-web/fl';
import { initials } from './shared/initials.ts';
import { TWO_FACTOR_LABEL } from './account/signin-model.ts';
import { onCompanyHost, providerStart, useSecondStep, useSignIn, type SignedIn } from './login-model.ts';
import { GoogleMark, MicrosoftMark } from './ProviderMarks.tsx';
import './sign-in-page.css';

/** A refusal (the server's words, unchanged): ink words after StateMark's red bar, announced. */
const Refused = ({ text }: { text: string }) => (
  <p className="signinpage-refused" role="alert">
    <StateMark state="problem" />
    {text}
  </p>
);

type Box = { current: HTMLInputElement | null };

/**
 * A field still empty when Sign in or Continue is pressed: it's marked with TextField's own error ("Enter
 * your email."), takes the focus, and nothing is sent; typing in it clears the error.
 */
function useEmptyCheck() {
  const [empty, setEmpty] = useState<Box | null>(null);
  const firstEmpty = (boxes: readonly Box[]): boolean => {
    const box = boxes.find((b) => b.current && !b.current.readOnly && !b.current.value.trim()) ?? null;
    setEmpty(box);
    box?.current?.focus();
    return !!box;
  };
  /** A field's error while it's the empty one, and its onChange, which clears that. */
  const field = (box: Box, words: string, onChange: (value: string) => void) => ({
    error: empty === box ? words : null,
    onChange: (value: string) => {
      if (empty === box) setEmpty(null);
      onChange(value);
    },
  });
  return { firstEmpty, field };
}

export function SignInPage({
  onSignedIn,
  tenant: knownTenant,
  again = false,
  brand,
  companyHost = onCompanyHost,
}: {
  /** recoveryCodesLeft: set when the second step was a recovery code, so the app can say how many remain. */
  onSignedIn: SignedIn;
  /** Signing in again after the session ended: the company is known and the screen stays behind. */
  tenant?: string;
  again?: boolean;
  /** The company, once its host says before sign-in: its name and logo beside the title. */
  brand?: { name: string; logoUrl?: string | null };
  /** On a company's own host the host says whose sign-in it is; off one (local development) the form asks. */
  companyHost?: boolean;
}) {
  const s = useSignIn({ onSignedIn, tenant: knownTenant, again, companyHost });
  const { mode, error, busy, second, company } = s;
  const shown = brand ?? (second && s.stepCompany ? { name: s.stepCompany, logoUrl: null } : undefined);
  const companyBox = useRef<HTMLInputElement | null>(null);
  const emailBox = useRef<HTMLInputElement | null>(null);
  const mobileBox = useRef<HTMLInputElement | null>(null);
  const passwordBox = useRef<HTMLInputElement | null>(null);
  const codeBox = useRef<HTMLInputElement | null>(null);
  const { firstEmpty, field } = useEmptyCheck();
  const checked = (send: (e: FormEvent) => Promise<void>, boxes: Box[]) => (e: FormEvent) => {
    e.preventDefault();
    if (!firstEmpty(boxes)) void send(e);
  };

  const companyField = s.asksCompany && (
    <TextField
      label="Company"
      value={s.tenant}
      {...field(companyBox, 'Enter the company name.', s.setTenant)}
      description="The company name from your invite"
      inputRef={companyBox}
    />
  );
  const emailField = (
    <TextField
      label="Email"
      type="email"
      value={s.email}
      {...field(emailBox, 'Enter your email.', s.setEmail)}
      autoComplete="username"
      isReadOnly={mode.kind === 'code' && !!mode.sent}
      inputRef={emailBox}
    />
  );

  return (
    <main
      className={again ? 'signinpage signinpage-again' : 'signinpage bp-sheet'}
      role={again ? 'dialog' : undefined}
      aria-modal={again || undefined}
    >
      <div className="signinpage-sheet">
        {shown && (
          <p className="signinpage-co">
            <span className="signinpage-logo login-logo" aria-hidden="true">
              {shown.logoUrl ? <img src={shown.logoUrl} alt="" /> : initials(shown.name)}
            </span>
            {shown.name}
          </p>
        )}
        {!second && <h1 className="signinpage-title">Sign in</h1>}
        {again && (
          <p className="signinpage-lede">Your session ended. Sign in again to carry on where you were.</p>
        )}
        {error && <Refused text={error} />}
        {second ? (
          <SecondStep
            methods={second}
            {...(company ? { tenant: company } : {})}
            onSignedIn={onSignedIn}
            onCancel={s.startAgain}
          />
        ) : (
          <>
            {(s.google || s.microsoft) && (
              <>
                <div className="signinpage-providers" role="group" aria-label="Sign in with">
                  {s.google && (
                    <Link size="touch-on-phone" href={providerStart('google', company)}>
                      <GoogleMark />
                      Continue with Google
                    </Link>
                  )}
                  {s.microsoft && (
                    <Link size="touch-on-phone" href={providerStart('microsoft', company)}>
                      <MicrosoftMark />
                      Continue with Microsoft
                    </Link>
                  )}
                </div>
                <p className="signinpage-or" aria-hidden="true">
                  or
                </p>
              </>
            )}
            {mode.kind === 'password' ? (
              <form
                className="signinpage-form"
                noValidate
                onSubmit={checked(s.submitPassword, [companyBox, emailBox, passwordBox])}
              >
                {companyField}
                {emailField}
                {s.byPassword && (
                  <TextField
                    label="Password"
                    type="password"
                    value={s.password}
                    {...field(passwordBox, 'Enter your password.', s.setPassword)}
                    autoComplete="current-password"
                    inputRef={passwordBox}
                  />
                )}
                {s.byPassword && (
                  <Button variant="primary" size="touch-on-phone" type="submit" isBusy={busy}>
                    {busy ? 'Signing in…' : 'Sign in'}
                  </Button>
                )}
              </form>
            ) : (
              <form
                className="signinpage-form"
                noValidate
                onSubmit={checked(s.submitCode, [
                  companyBox,
                  mode.channel === 'email' ? emailBox : mobileBox,
                  ...(mode.sent ? [codeBox] : []),
                ])}
              >
                {companyField}
                {mode.channel === 'email' ? (
                  emailField
                ) : (
                  <TextField
                    label="Mobile"
                    type="tel"
                    value={s.mobile}
                    {...field(mobileBox, 'Enter your mobile.', s.setMobile)}
                    autoComplete="tel"
                    isReadOnly={!!mode.sent}
                    inputRef={mobileBox}
                  />
                )}
                {mode.sent && (
                  <>
                    <Notice tone="done" title={mode.sent} />
                    <TextField
                      label="Code"
                      value={s.code}
                      {...field(codeBox, 'Enter the code.', s.setCode)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      inputRef={codeBox}
                    />
                  </>
                )}
                <Button variant="primary" size="touch-on-phone" type="submit" isBusy={busy}>
                  {busy
                    ? 'One moment…'
                    : mode.sent
                      ? 'Sign in'
                      : mode.channel === 'email'
                        ? 'Email me a code'
                        : 'Text me a code'}
                </Button>
                {mode.sent && (
                  <span className="signinpage-again-code">
                    <Link size="touch-on-phone" onPress={() => s.setMode({ ...mode, sent: null })}>
                      Send a new code
                    </Link>
                  </span>
                )}
              </form>
            )}
            <div className="signinpage-other" role="group" aria-label="Other ways to sign in">
              {s.byEmail && !(mode.kind === 'code' && mode.channel === 'email') && (
                <Link
                  size="touch-on-phone"
                  onPress={() => s.switchTo({ kind: 'code', channel: 'email', sent: null })}
                >
                  Email me a code
                </Link>
              )}
              {s.byText && !(mode.kind === 'code' && mode.channel === 'sms') && (
                <Link
                  size="touch-on-phone"
                  onPress={() => s.switchTo({ kind: 'code', channel: 'sms', sent: null })}
                >
                  Text me a code
                </Link>
              )}
              {!s.byEmail && mode.kind === 'password' && (
                // The owner can always get in with an email code, whatever the company allows.
                <Link
                  size="touch-on-phone"
                  onPress={() => s.switchTo({ kind: 'code', channel: 'email', sent: null })}
                >
                  Company owner? Email me a code
                </Link>
              )}
              {s.byPassword && mode.kind === 'code' && (
                <Link size="touch-on-phone" onPress={() => s.switchTo({ kind: 'password' })}>
                  Use my password
                </Link>
              )}
              {s.byPassword && s.byEmail && mode.kind === 'password' && (
                <p className="signinpage-note">Forgot your password? An email code signs you in.</p>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}

/** The second step (decision 20, QA-093): its own title, the code, "Remember this device", the other ways. */
function SecondStep({
  methods,
  tenant,
  onSignedIn,
  onCancel,
}: {
  methods: readonly TwoFactorMethod[];
  tenant?: string;
  onSignedIn: SignedIn;
  onCancel: () => void;
}) {
  const t = useSecondStep({ methods, tenant, onSignedIn });
  const codeBox = useRef<HTMLInputElement | null>(null);
  const { firstEmpty, field } = useEmptyCheck();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!firstEmpty([codeBox])) void t.submit(e);
  };
  return (
    <form className="signinpage-form" noValidate onSubmit={submit} aria-label="Second step">
      <h1 className="signinpage-title">{t.label.title}</h1>
      <p className="signinpage-lede">{t.lede}</p>
      {t.error && <Refused text={t.error} />}
      {t.method === 'sms' && !t.texted ? (
        <Button variant="primary" size="touch-on-phone" isBusy={t.busy} onPress={() => void t.sendText()}>
          {t.busy ? 'Sending…' : 'Text me a code'}
        </Button>
      ) : (
        <>
          <TextField
            label={t.label.field}
            value={t.code}
            {...field(codeBox, 'Enter the code.', t.setCode)}
            inputMode={t.method === 'recovery' ? 'text' : 'numeric'}
            autoComplete={t.method === 'recovery' ? 'off' : 'one-time-code'}
            autoFocus
            inputRef={codeBox}
          />
          <Checkbox size="touch-on-phone" isSelected={t.remember} onChange={t.setRemember}>
            Remember this device for 30 days
          </Checkbox>
          <Button variant="primary" size="touch-on-phone" type="submit" isBusy={t.busy}>
            {t.busy ? 'Checking…' : 'Continue'}
          </Button>
        </>
      )}
      <div className="signinpage-other" role="group" aria-label="Other ways to confirm it’s you">
        {t.others.map((m) => (
          <Link key={m} size="touch-on-phone" onPress={() => t.switchTo(m)}>
            {TWO_FACTOR_LABEL[m].switchTo}
          </Link>
        ))}
        <p className="signinpage-note">Lost every way in? Ask the owner to reset your two-factor.</p>
        <Link size="touch-on-phone" onPress={onCancel}>
          Start again
        </Link>
      </div>
    </form>
  );
}
